<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});
const { t } = useI18n();
const hasError = ref(false);
const showGallery = ref(false);

const moderation = computed(
  () =>
    props.attachment.imageModeration || props.attachment.image_moderation || {}
);
const isBlocked = computed(
  () => moderation.value.blocked === true && moderation.value.approved !== true
);
const blockedImageLabel = computed(() =>
  t('CONVERSATION.REPLYBOX.SENSITIVE_IMAGE_HIDDEN_LABEL', {
    category: moderation.value.category || 'pendente',
    description:
      props.attachment.imageDescription ||
      props.attachment.image_description ||
      moderation.value.reason ||
      '',
  })
);

const { filteredCurrentChatAttachments } = useMessageContext();

const handleError = () => {
  hasError.value = true;
};
</script>

<template>
  <div
    v-if="isBlocked"
    role="img"
    :aria-label="blockedImageLabel"
    class="flex size-[72px] flex-col items-center justify-center gap-1 rounded-xl border border-n-ruby-7 bg-n-ruby-2 p-2 text-center text-n-ruby-11"
  >
    <Icon aria-hidden="true" icon="i-lucide-shield-alert" class="size-5" />
    <span class="text-[10px] font-medium leading-tight">
      {{ t('CONVERSATION.REPLYBOX.SENSITIVE_IMAGE_HIDDEN_SHORT') }}
    </span>
  </div>
  <div
    v-else
    role="button"
    tabindex="0"
    :aria-label="
      attachment.imageDescription ||
      attachment.image_description ||
      'Abrir imagem anexada'
    "
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
    @keydown.enter.prevent="showGallery = true"
    @keydown.space.prevent="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <img
      v-else
      class="object-cover w-full h-full skip-context-menu"
      :src="attachment.dataUrl"
      :alt="
        attachment.imageDescription ||
        attachment.image_description ||
        'Imagem anexada sem descrição'
      "
      @error="handleError"
    />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
